import EventEmitter from '../../utils/events'
import handlers from './ws.dispatcher'
import config from '../../config'
import { IDataMessage, IWSDataSubscription, WsMessage } from './ws.types'
import { RefObject } from 'react'
import eventBus from '../eventBus'
import { IAuthState } from '@/pages/Auth/auth.atom.ts'
import dataUtils from '../data/data.utils.ts'
import { IParameter } from '@/pages/Parameters/parameters.types.ts'

const STATES = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3,
}

class WSClient {
  public connected$ = new EventEmitter<boolean>()

  private parametersRecord: Record<IParameter['sourceId'], IParameter> = {}
  private auth: RefObject<IAuthState>
  private ws: WebSocket
  private conntected = false

  private reconnectInterval = 3000 // 3s

  private timer = null
  private isReconnecting = false

  constructor() {}

  public setParametersRecord(data: Record<IParameter['sourceId'], IParameter>) {
    this.parametersRecord = data
  }

  public connect(authRef: RefObject<IAuthState> = null) {
    if (this.ws) {
      return 'already connected'
    }

    if (authRef) {
      this.auth = authRef
    }

    if (!this.auth.current.accessToken) {
      return console.log('ws: aborting connect, not authorized')
    }

    const ws = new WebSocket(config.WS_URL + `/ws/?accessToken=${this.auth.current.accessToken}`)
    this.ws = ws
    this.ws.binaryType = 'arraybuffer'

    ws.addEventListener('close', this.onclose)
    ws.addEventListener('message', this.onmessage)
    ws.addEventListener('open', this.onopen)
  }

  public async dataSubscribe(data: IWSDataSubscription) {
    if (!this.conntected) {
      await new Promise((res) => {
        this.connected$.subscribe((val) => {
          if (val) res(true)
        })
      })
    }

    this.ws.send(JSON.stringify(data))
  }

  private terminate() {
    clearTimeout(this.timer)

    if (!this.ws) {
      return 'already terminated'
    }

    this.conntected = false
    this.ws.removeEventListener('close', this.onclose)
    this.ws.removeEventListener('message', this.onmessage)
    this.ws.removeEventListener('open', this.onopen)

    const state = this.ws.readyState
    if (state === STATES.OPEN || state === STATES.CONNECTING) {
      this.ws.close()
    }

    this.ws = null
  }

  private onclose = (e?: CloseEvent) => {
    this.terminate()
    this.connected$.next(false)

    // переподключиться, если соединение закрылось с ошибкой
    if (e && e.code !== 1000) {
      this.isReconnecting = true
      this.timer = setTimeout(() => this.connect(), this.reconnectInterval)
    }
  }

  private onmessage = (message) => {
    const { data } = message

    if (data instanceof ArrayBuffer) {
      console.warn('Received binary message')
    } else {
      return this.handleJSONMessage(data.toString())
    }
  }

  private onopen = () => {
    console.log('WS: connected to server')

    if (this.isReconnecting) {
      this.isReconnecting = false

      console.log('WS: reconnected, reloading resources')
    }

    this.conntected = true
    this.connected$.next(true)
  }

  private handleJSONMessage(content: string) {
    let msg: WsMessage | IDataMessage = null

    try {
      msg = JSON.parse(content)
    } catch (error) {
      console.error(error)
      return
    }

    if (msg && 'deviceId' in msg) {
      return eventBus.pub('data.point', dataUtils.transformDataMessage(msg, this.parametersRecord))
    }

    msg = msg as WsMessage
    if (!handlers?.[msg.resource]?.[msg.method]) {
      console.warn('Unsupported message type: ', msg)
    } else {
      try {
        // @ts-ignore
        handlers[msg.resource][msg.method](msg)
      } catch (err) {
        console.error('Error during message handle: ', JSON.stringify(msg), err)
      }
    }
  }
}

const ws = new WSClient()
export default ws
